const ENCRYPTION_KEY_ITEM = 'jcSecureSessionStorageKey';
const AES_GCM_IV_LENGTH = 12;

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';

  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
  const binary = atob(base64);

  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
}

async function getOrCreateKey(): Promise<CryptoKey> {
  const storedKey = sessionStorage.getItem(ENCRYPTION_KEY_ITEM);

  if (storedKey) {
    return crypto.subtle.importKey(
      'raw',
      base64ToBytes(storedKey),
      { name: 'AES-GCM' },
      false,
      ['encrypt', 'decrypt'],
    );
  }

  const rawKey = crypto.getRandomValues(new Uint8Array(32));
  sessionStorage.setItem(ENCRYPTION_KEY_ITEM, bytesToBase64(rawKey));

  return crypto.subtle.importKey(
    'raw',
    rawKey,
    { name: 'AES-GCM' },
    false,
    ['encrypt', 'decrypt'],
  );
}

async function encrypt(plaintext: string): Promise<string> {
  const key = await getOrCreateKey();
  const iv = crypto.getRandomValues(new Uint8Array(AES_GCM_IV_LENGTH));
  const encoded = new TextEncoder().encode(plaintext);
  const ciphertext = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
  const payload = new Uint8Array(iv.length + ciphertext.byteLength);

  payload.set(iv);
  payload.set(new Uint8Array(ciphertext), iv.length);

  return bytesToBase64(payload);
}

async function decrypt(encryptedPayload: string): Promise<string> {
  const key = await getOrCreateKey();
  const payload = base64ToBytes(encryptedPayload);
  const iv = payload.slice(0, AES_GCM_IV_LENGTH);
  const ciphertext = payload.slice(AES_GCM_IV_LENGTH);
  const decrypted = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ciphertext);

  return new TextDecoder().decode(decrypted);
}

/**
 * Stores sensitive values in sessionStorage after encrypting them with AES-GCM.
 */
export default {
  async setItem(keyName: string, keyValue: string): Promise<void> {
    const encryptedValue = await encrypt(keyValue);

    try {
      sessionStorage.setItem(keyName, encryptedValue);
    } catch (error) {
      throw error instanceof Error ? error : new Error(String(error));
    }

    if (sessionStorage.getItem(keyName) !== encryptedValue) {
      throw new Error('SecureSessionStorageService: failed to persist encrypted value');
    }
  },

  async getItem(keyName: string): Promise<string | null> {
    try {
      const encryptedValue = sessionStorage.getItem(keyName);

      if (!encryptedValue) {
        return null;
      }

      return await decrypt(encryptedValue);
    } catch {
      return null;
    }
  },

  removeItem(keyName: string): void {
    try {
      sessionStorage.removeItem(keyName);
    } catch {
      // Match LocalStorageService behavior for unavailable storage.
    }
  },
};
